import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardSubNav } from "@/components/AdSlot";
import { Plus, Pencil, FileText } from "lucide-react";

const STATUS_LABELS = {
  unclaimed: { label: "Unclaimed", cls: "bg-muted text-foreground/70" },
  claim_pending: { label: "Claim pending", cls: "bg-accent/15 text-accent-dark" },
  claimed: { label: "Claimed", cls: "bg-primary/10 text-primary" },
  approved: { label: "Approved", cls: "bg-primary/10 text-primary" },
  pending_new: { label: "Pending review", cls: "bg-accent/15 text-accent-dark" },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive" },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState(null);

  useEffect(() => {
    api.get("/me/listings").then((r) => setListings(r.data.results)).catch(() => setListings([]));
  }, []);

  return (
    <div className="container-page py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="chip">Dashboard</span>
          <h1 className="mt-3 font-display text-4xl font-bold text-primary lg:text-5xl">Welcome, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-foreground/70">Manage your listings, gallery, and blog posts.</p>
        </div>
        <Link to="/dashboard/listings/new" className="btn-accent" data-testid="submit-new-listing">
          <Plus className="h-4 w-4" /> Submit a new listing
        </Link>
      </div>

      <div className="mt-10">
        <DashboardSubNav active="listings" />
        <h2 className="mt-8 font-display text-2xl font-bold text-primary">My listings</h2>
        {listings === null ? (
          <p className="mt-4 text-muted-foreground">Loading…</p>
        ) : listings.length === 0 ? (
          <div className="card-surface mt-6 p-10 text-center" data-testid="dashboard-empty">
            <p className="font-medium text-foreground/80">You don't manage any listings yet.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Find your practice in the directory and click "Claim this listing" — or submit a new one.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/directory" className="btn-secondary">Browse directory</Link>
              <Link to="/dashboard/listings/new" className="btn-primary">Submit new</Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid gap-4">
            {listings.map((l) => {
              const status = STATUS_LABELS[l.status] || { label: l.status, cls: "bg-muted" };
              return (
                <div key={l.id} className="card-surface flex flex-wrap items-center gap-4 p-5" data-testid={`my-listing-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-xl font-bold text-primary">{l.practice_name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${status.cls}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {l.category} {l.city ? `· ${l.city}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(l.status === "claimed" || l.status === "approved") && (
                      <Link to={`/dashboard/listings/${l.id}`} className="btn-secondary !py-2 text-sm" data-testid={`manage-${l.id}`}>
                        <Pencil className="h-4 w-4" /> Manage
                      </Link>
                    )}
                    <Link to={`/listing/${l.id}`} className="btn-secondary !py-2 text-sm" data-testid={`view-${l.id}`}>
                      <FileText className="h-4 w-4" /> View
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
